import { Clock, Landmark, Plane, Trophy, Waves, TrendingUp, Globe2 } from "lucide-react";
import { Chapter, SubTitle, Body } from "@/components/Chapter";
import { FinTable } from "@/components/FinTable";
import { KpiPanel } from "@/components/KpiPanel";

const DRIVER_ICONS = [Landmark, Plane, Trophy, Waves, TrendingUp];

const FramedImage = ({ src, alt, testid }) => (
  <div data-testid={testid} className="relative">
    <div className="absolute -left-3 -top-3 h-full w-full border border-gold/50" />
    <div className="relative overflow-hidden">
      <img src={src} alt={alt} className="aspect-[16/9] w-full object-cover transition-transform duration-700 hover:scale-[1.03]" />
      <div className="absolute inset-0 bg-jade/10" />
    </div>
  </div>
);

export const CoreSections = ({ d }) => (
  <>
    <Chapter id="01" num="01" title="Resumen Ejecutivo">
      <KpiPanel kpis={d.kpiPanel} />
      <FinTable testid="tabla-resumen" columns={["Concepto", "Detalle"]} rows={d.resumen} />
    </Chapter>

    <Chapter id="02" num="02" title="El Proyecto">
      <FramedImage src={d.meta.images.proyecto} alt="Arquitectura tropical del complejo Jade Residence" testid="proyecto-image" />
      <div>
        <SubTitle>Ubicación estratégica</SubTitle>
        <Body className="mt-3">{d.proyecto.ubicacionIntro}</Body>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {d.proyecto.ubicacionBullets.map((b, i) => (
            <div key={i} data-testid={`ubicacion-item-${i}`} className="flex items-center gap-3 rounded-sm border border-sand bg-white/70 px-4 py-3.5">
              <Clock className="h-4 w-4 shrink-0 text-gold" />
              <span className="text-sm text-graphite/80">{b}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <SubTitle>El complejo</SubTitle>
        <Body className="mt-3">{d.proyecto.complejo}</Body>
      </div>
    </Chapter>

    <Chapter id="03" num="03" title="El Mercado de Lombok">
      <FinTable
        testid="tabla-mercado"
        columns={["Métrica", "Valor", "Fuente"]}
        rows={d.mercado.datos}
        rightFrom={2}
      />
      <div>
        <SubTitle>Origen de los huéspedes</SubTitle>
        <Body className="mt-3">{d.mercado.origenIntro}</Body>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {d.mercado.origen.map((o, i) => (
            <div key={o.titulo} data-testid={`origen-card-${i}`} className="rounded-sm border border-sand bg-white/85 p-6 transition-colors duration-300 hover:border-gold/60">
              <Globe2 className="h-5 w-5 text-gold" />
              <p className="mt-3 font-serif text-lg text-jade">{o.titulo}</p>
              <p className="mt-2 text-xs leading-relaxed text-graphite/65">{o.texto}</p>
            </div>
          ))}
        </div>
      </div>
    </Chapter>

    <Chapter id="04" num="04" title="Drivers de Crecimiento">
      <FramedImage src={d.meta.images.mercado} alt="Resort tropical en Lombok" testid="drivers-image" />
      <div className="space-y-0 divide-y divide-sand/70 border-y border-sand/70">
        {d.drivers.map((drv, i) => {
          const Icon = DRIVER_ICONS[i % DRIVER_ICONS.length];
          return (
            <div key={drv.titulo} data-testid={`driver-item-${i}`} className="group flex items-start gap-5 py-6 transition-colors duration-300 hover:bg-white/60 sm:gap-8 sm:px-4">
              <span className="font-serif text-2xl font-light italic text-gold/70">0{i + 1}</span>
              <Icon className="mt-1 h-5 w-5 shrink-0 text-jade" />
              <div>
                <p className="font-serif text-lg text-graphite transition-colors duration-300 group-hover:text-jade sm:text-xl">
                  {drv.titulo}
                </p>
                <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-graphite/65">{drv.texto}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Chapter>

    <Chapter id="05" num="05" title="Supuestos del Modelo">
      <div>
        <SubTitle>Tarifas por noche · Escenario base</SubTitle>
        <div className="mt-6">
          <FinTable testid="tabla-tarifas" columns={["Temporada", "Precio / noche"]} rows={d.supuestos.tarifas} />
        </div>
      </div>
    </Chapter>

    <Chapter id="06" num="06" title="Estructura de Costes">
      <Body className="italic text-graphite/55">{d.costes.intro}</Body>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {[d.costes.operational, d.costes.community].map((block) => (
          <div key={block.titulo}>
            <div className="mb-4 flex items-baseline justify-between">
              <SubTitle>{block.titulo}</SubTitle>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{block.subtitulo}</span>
            </div>
            <FinTable testid={`tabla-${block.titulo === "Operational Expenses" ? "opex" : "community"}`} rows={block.rows} />
          </div>
        ))}
      </div>
      <div>
        <SubTitle>Otros costes</SubTitle>
        <div className="mt-5">
          <FinTable testid="tabla-otros-costes" rows={d.costes.otros} />
        </div>
      </div>
    </Chapter>

    <Chapter id="07" num="07" title="Reparto de Ingresos · De cada €100">
      <Body>{d.reparto.intro}</Body>
      <div data-testid="reparto-bar" className="flex h-14 w-full overflow-hidden rounded-sm border border-sand">
        {d.reparto.rows.map((r, i) => {
          const row = Array.isArray(r) ? r : r.cells;
          const pct = parseFloat(String(row[1]).replace(/[~%]/g, "").replace(",", "."));
          const COLORS = ["#6B8F7D", "#2C5446", "#A67C52", "#1F3A2E", "#E4DDCC", "#CBA153"];
          return (
            <div
              key={i}
              data-testid={`reparto-segment-${i}`}
              className="flex items-center justify-center transition-opacity duration-300 hover:opacity-85"
              style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }}
              title={`${row[0]} · ${row[1]}`}
            >
              <span className={`hidden text-[10px] font-semibold sm:block ${i === 4 ? "text-graphite/70" : "text-ivory"}`}>{row[1]}</span>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {d.reparto.rows.map((r, i) => {
          const row = Array.isArray(r) ? r : r.cells;
          const COLORS = ["#6B8F7D", "#2C5446", "#A67C52", "#1F3A2E", "#E4DDCC", "#CBA153"];
          return (
            <span key={i} data-testid={`reparto-legend-${i}`} className="inline-flex items-center gap-2 text-xs text-graphite/70">
              <span className="h-2.5 w-2.5 rotate-45" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              {row[0]}
            </span>
          );
        })}
      </div>
      <FinTable
        testid="tabla-reparto"
        columns={["Destino", "%", "€ de cada €100"]}
        rows={d.reparto.rows}
        rightFrom={1}
        note={d.reparto.nota}
      />
    </Chapter>

    <Chapter id="08" num="08" title="Calendario de Hitos de Pago">
      <Body>{d.hitos.intro}</Body>
      <div data-testid="hitos-timeline" className="rounded-sm border border-sand bg-white/85 shadow-[0_4px_24px_rgba(31,110,90,0.05)]">
        {d.hitos.rows.filter((r) => Array.isArray(r)).map((r, i) => (
          <div key={i} data-testid={`hito-item-${i}`} className="flex items-center gap-4 border-b border-sand/70 px-5 py-5 sm:gap-6 sm:px-7">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/60 font-serif text-base italic text-gold">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-serif text-lg leading-snug text-graphite">{r[1]}</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-graphite/50">{r[0]}</p>
            </div>
            <span className="hidden font-mono text-xs text-jade sm:block">{r[2]}</span>
            <span className="w-24 shrink-0 text-right font-serif text-xl font-semibold text-jade">{r[3]}</span>
          </div>
        ))}
        {d.hitos.rows.filter((r) => !Array.isArray(r)).map((r, i) => (
          <div key={`total-${i}`} data-testid="hito-total" className="flex items-center justify-between border-t-2 border-gold/60 bg-gold/[0.08] px-5 py-4 sm:px-7">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-graphite/70">Total inversión</span>
            <span className="font-serif text-2xl font-semibold text-jade">{r.cells[3]}</span>
          </div>
        ))}
      </div>
      <p className="max-w-3xl text-xs italic leading-relaxed text-graphite/55">{d.hitos.nota}</p>
    </Chapter>
  </>
);
